/**
 * 2-column app shell for every onboarding page.
 *
 * Desktop (lg+): top chrome (logo + exit link) + stepper across the top +
 *   2-column body (form/content left, sticky outcome rail right).
 * Mobile (<lg): single column. Rail card placed ABOVE the H1 so progress
 *   is visible before the form.
 *
 * Decoupled from useAuth — pages compute the rail state from me.* fields
 * and pass it down. This keeps the shell pure layout.
 *
 * See STATUS/PLAN_ONBOARDING_REDESIGN_PORT.md.
 */

import { Link } from 'react-router-dom';
import { OnboardingStepper } from '@/components/OnboardingStepper';
import { OnboardingRail, type OnboardingRailState } from '@/components/onboarding/OnboardingRail';

interface Props {
  current: 1 | 2 | 3 | 4 | 'success';
  title: string;
  subtitle?: string;
  state: OnboardingRailState;
  children: React.ReactNode;
}

export function OnboardingShell({ current, title, subtitle, state, children }: Props) {
  const stepperCurrent = (current === 'success' ? 4 : current) as 1 | 2 | 3 | 4;

  return (
    <div className="min-h-screen flex flex-col bg-bg-tint">
      {/* Top chrome — logo left, exit right */}
      <header className="w-full border-b border-dark/5 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center">
            <span className="font-extrabold text-xl tracking-tight">
              <span
                style={{
                  WebkitTextStroke: '2px #0F172A',
                  color: 'white',
                  paintOrder: 'stroke fill',
                }}
              >
                hey
              </span>
              <span className="text-brand">motek</span>
            </span>
          </Link>
          {state.profileDone && (
            <Link
              to="/"
              className="text-sm font-medium text-muted-foreground hover:text-dark transition-colors"
            >
              Exit setup
            </Link>
          )}
        </div>
      </header>

      {/* Body */}
      <div className="flex-1 mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        {/* Stepper — full width */}
        <div className="mb-8 lg:mb-10">
          <OnboardingStepper current={stepperCurrent} />
        </div>

        {/* 2-column grid */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6 lg:gap-10">
          {/* Left: content */}
          <div className="space-y-6">
            {/* Mobile rail — full card above H1, hidden on lg+ */}
            <div className="lg:hidden">
              <OnboardingRail state={state} variant="mobile" />
            </div>

            {/* Header */}
            <div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-dark">
                {title}
              </h1>
              {subtitle && (
                <p className="mt-3 text-base sm:text-lg text-muted-foreground max-w-prose">
                  {subtitle}
                </p>
              )}
            </div>

            {/* Page content */}
            {children}
          </div>

          {/* Desktop rail — only visible on lg+ */}
          <div className="hidden lg:block">
            <OnboardingRail state={state} variant="desktop" />
          </div>
        </div>
      </div>
    </div>
  );
}
